<script setup lang="ts">

import { ref, watch, nextTick, reactive } from 'vue'

import type { IEventGuest, IHostPortalTheme } from '../../types/EventTypes'

import { resolveColumnWidth } from '@/utils/kendoHelper.ts'

import { GetHostDashboardUrl, GetHostPortalUrl, GetSiteHomeofficeUrl } from '@/stores/SettingsStore.ts'

import { Checkbox as KCheckBox } from '@progress/kendo-vue-inputs'

const showSendEmailPopup = ref(false)

const sendEmailGuest = ref<IEventGuest | null>(null)

const emailSubject = ref('')

const emailBody = ref('')

const hostDashboardURL = ref<string>('')

const hostPortalURL = ref<string>('')

const siteHomeofficeUrl = ref<string>('')

const savedScrollPosition = ref<number>(0)



function openSendEmailPopup(guest: IEventGuest) {

  if (savedEventStatus.value === 'C') {

    return;

  }

  sendEmailGuest.value = guest;

  emailSubject.value = '';

  emailBody.value = '';

  // Save current scroll position before opening popup

  savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop;

  showSendEmailPopup.value = true;

  // Prevent body scrolling when popup is open

  document.body.style.overflow = 'hidden';

}



function closeSendEmailPopup() {

  showSendEmailPopup.value = false

  // Restore body scrolling when popup is closed

  document.body.style.overflow = ''

  // Restore scroll position after a brief delay to ensure DOM is updated

  setTimeout(() => {

    window.scrollTo(0, savedScrollPosition.value);

  }, 10);

}



async function sendGuestEmail() {

  try {

    if (!sendEmailGuest.value) {

      logger.error('No guest selected for email')

      return

    }



    if (!emailSubject.value || !emailBody.value) {

      await Swal.fire({

        icon: 'warning',

        title: t('alerts.ValidationError'),

        text: t('eventManagement.EmailSubjectAndBodyRequired'),

      })

      return

    }



    // Get consultant email from emailFrom or use a default

    const fromEmail = emailFrom.value || eventHostEmail.value || 'noreply@example.com'

    const toEmail = sendEmailGuest.value.email



    if (!toEmail) {

      await Swal.fire({

        icon: 'error',

        title: t('alerts.Error'),

        text: t('eventManagement.GuestEmailNotAvailable'),

      })

      return

    }



    logger.debug('Sending email to guest:', toEmail)



    const emailRequest = {

      fromEmailAddress: fromEmail,

      toEmailAddress: toEmail,

      emailSubject: emailSubject.value,

      emailBody: emailBody.value,

    }



    const response = await SendEmail(emailRequest)



    if (response.success) {

      await Swal.fire({

        icon: 'success',

        title: t('alerts.Success'),

        text: t('eventManagement.EmailSentSuccessfully'),

      })

      closeSendEmailPopup()

    } else {

      await Swal.fire({

        icon: 'error',

        title: t('alerts.Error'),

        text: response.message || t('eventManagement.EmailSendFailed'),

      })

    }

  } catch (error) {

    logger.error('Error sending email:', error)

    await handleError(error, t)

  }

}



// Handle keyboard events for popup

function handleKeydown(event: KeyboardEvent) {

  if (event.key === 'Escape' && showSendEmailPopup.value) {

    closeSendEmailPopup()

  }

}



import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

import { useDatePickerLocalization } from '../../composables/useDatePickerLocalization'

import { useLogger } from 'vue-logger-plugin'

import { onMounted, onUnmounted } from 'vue'

import { setStoreLogger } from '../../logger.ts'



import { Button as KButton } from '@progress/kendo-vue-buttons'

import { Popup } from '@progress/kendo-vue-popup'

import { Menu as kMenu } from '@progress/kendo-vue-layout'



import {

  PanelBar,

  TabStrip as kTabStrip,

  type TabStripSelectEventArguments,

} from '@progress/kendo-vue-layout'

import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'

import { DropDownList } from '@progress/kendo-vue-dropdowns'

import {

  DatePicker,

  TimePicker,

  type DatePickerChangeEvent,

} from '@progress/kendo-vue-dateinputs'

import { LocalizationProvider } from '@progress/kendo-vue-intl'

import { Grid, type GridCellProps } from '@progress/kendo-vue-grid'

import { trashIcon, moreVerticalIcon, fileTxtIcon } from '@progress/kendo-svg-icons'



import Swal from 'sweetalert2'



import AddressControl from './AddressControl.vue'

import FindEventContactsControl from './FindEventContactsControl.vue'

import VerifyAddressPopup from '../VerifyAddressPopup.vue'

import PhoneInputControl from '../PhoneInputControl.vue'



import type {

  IAddress,

  IAddressVerificationListItem,

} from '../../types/CountryCultureTypes'



import type { IPhoneInput, IPhoneOutput } from '../../types/CommonTypes'

import type { IAoeCart } from '../../types/CartTypes'

import type { IAoeCustomer } from '../../types/CustomerTypes.js'

import { createRequiredFieldValidator, createEmailFieldValidator, createNameFieldValidator, createPhoneFieldValidator } from '../../validators/StringValidator.ts'



import { formatDateForJSONRequest, formatDateTimeForJSONRequest } from '../../api/Common.ts'



import {

  EventGet,

  EventGuestGetList,

  EventGuestCreate,

  EventUpdate,

  EventDelete,

  AddressAdd,

  EventGuestDelete,

  EventGuestMakeHost,

  GetHostPortalThemes,

  GetHostPortalDetail,

  EventMasterOrderGet,

} from '../../services/EventService'

import { CartShippingAddressCreate } from '../../api/Cart'

import { HostPortalEventGet, HostPortalEventCreate, HostPortalEventUpdate, SendEmail } from '../../api/Event'

import { HostPortalAddressBookCreate } from '../../services/AddressBookService'

import { GetCodesFromLocalStorageOrApi, GetCountriesFromLocalStorageOrApi } from '../../services/CountryCultureService'

import { FormatCurrency } from '../../services/currencyFormatter'

import { handleError } from '../../services/ErrorService.ts'

import {

  GetBeeNumberAuthenicatedUser,

  GetEventListUrl,

  GetPartyOrderEntryUrl,

  GetShopUrl,

  GetCultureName,

  GetOrderReceiptUrl,

} from '../../stores/SettingsStore.ts'

import { GetConsultantFromLocalStorageOrApi } from '../../services/ConsultantService'

import { useConsultantOwnEmailGuard } from '../../composables/useConsultantOwnEmailGuard'

import { SetIsLoading } from '../../services/AoeHelpers'

import type { ICategoryCode } from '../../types/CategoryCodeTypes'



const logger = useLogger()

const { loadConsultantEmailAddress, isConsultantOwnEmail } = useConsultantOwnEmailGuard()



const { dateFormat, formatPlaceholder, timeFormat } = useDatePickerLocalization(locale)

// @progress/kendo-vue-dateinputs' TimePicker.d.ts mistypes the `format` prop
// as the literal "t" (its default value) instead of `string | DateFormatOptions`
// (its actual runtime type — see TimePickerSettings.format, and compare to
// DatePicker's own correctly-typed `format`). Cast once here so the template
// binding below type-checks without needing an `any` cast at every usage.
const timePickerFormat = computed(() => timeFormat.value as unknown as 't')

setStoreLogger(logger)

logger.debug('AOEEventManagement.vue')



const canBeDeleted = ref<boolean>(true)

const beeNumberAuth = ref<number | null>(0)

const eventListURL = ref<string>('')

const eventTitle = ref<string>('')

const eventID = ref<number>(0)

const eventIdToken = ref<string>('')

const eventType = ref<string>('')

const selectedEventStatus = ref<ICategoryCode>({ text: '', id: '' })

const savedEventStatus = ref<string>('') // Tracks the actual saved status from database

const txtEventDate = ref<Date | undefined>(undefined)

const txtEventStartTime = ref<Date | undefined>(undefined)

const txtEventEndTime = ref<Date | undefined>(undefined)

// Original date/time values to track changes

const txtEventDateOrig = ref<Date | undefined>(undefined)

const txtEventStartTimeOrig = ref<Date | undefined>(undefined)

const txtEventEndTimeOrig = ref<Date | undefined>(undefined)

const eventLocationAddressId = ref<number | null>(0)

const eventLocationAddressLine1 = ref<string>('')



// --- Cart Page URL and Copy Logic ---

import { computed } from 'vue'

const cartUrlCopied = ref(false)

// Use the already declared cartDetailURL



const cartPageUrl = computed(() => {

  if (cartDetailURL.value && eventIdToken.value) {

    let baseUrl = cartDetailURL.value

    // If not absolute, prepend window.location.origin

    if (!/^https?:\/\//i.test(baseUrl)) {

      baseUrl = window.location.origin + (baseUrl.startsWith('/') ? '' : '/') + baseUrl

    }

    return baseUrl + '?event=' + eventIdToken.value

  }

  return ''

})



async function copyCartUrlToClipboard() {

  try {

    await navigator.clipboard.writeText(cartPageUrl.value)

    cartUrlCopied.value = true

    setTimeout(() => {

      cartUrlCopied.value = false

    }, 2000)

  } catch {

    cartUrlCopied.value = false

    alert(t('eventManagement.CopyFailed'))

  }

}

// --- End Cart Page URL and Copy Logic ---



const eventLocationAddressLine2 = ref<string | null>('')

const eventLocationAddressHouseNumber = ref<string | null>('')

const eventLocationAddressCity = ref<string>('')

const eventLocationAddressStateCode = ref<string | null>('')

const eventLocationAddressPostalCode = ref<string>('')

const eventLocationAddressCountryCode = ref<string>('')



const eventLocationAddressIdOrig = ref<number | null>(0)

const eventLocationAddressLine1Orig = ref<string>('')

const eventLocationAddressLine2Orig = ref<string | null>('')

const eventLocationAddressHouseNumberOrig = ref<string | null>('')

const eventLocationAddressCityOrig = ref<string>('')

const eventLocationAddressStateCodeOrig = ref<string | null>('')

const eventLocationAddressPostalCodeOrig = ref<string>('')

const eventLocationAddressCountryCodeOrig = ref<string>('')

const eventLocationAddressFirstName = ref<string | null>(null)

const eventLocationAddressLastName = ref<string | null>(null)

const eventHostName = ref<string>('')

const eventHostFirstName = ref<string>('')

const eventHostLastName = ref<string>('')

const eventHostEmail = ref<string>('')

const eventHostPhoneNumber = ref<string>('')



// Guest form state

const formInitialValues = ref<IAoeCustomer>({ } as IAoeCustomer)



// Validators for guest form (kept for reference, but manual validation used instead)

const firstNameValidator = [createRequiredFieldValidator(t, 'firstName'), createNameFieldValidator(t, 'firstName')]

const lastNameValidator = [createRequiredFieldValidator(t, 'lastName'), createNameFieldValidator(t, 'lastName')]

const emailValidator = [createRequiredFieldValidator(t, 'emailAddress'), createEmailFieldValidator(t, 'emailAddress')]

const phoneNumberValidator = [createPhoneFieldValidator(t, 'phone', eventLocationAddressCountryCode.value)]



// Reactive errors object for manual validation

const errors = reactive({

  firstName: '',

  lastName: '',

  emailAddress: ''

})



// Phone control ref for validation

const guestPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Address control ref for validation
const addressControlRef = ref<InstanceType<typeof AddressControl> | null>(null)



interface IGuestPhoneCountry {

  text: string

  id: string

}

const guestPhoneCountries = ref<IGuestPhoneCountry[]>([])

const guestPhoneModel = ref<IPhoneOutput | null>(null)



function newGuestPhoneModel(): IPhoneOutput {

  return {

    number: '',

    countryCode: eventLocationAddressCountryCode.value || '',

    formattedNumber: '',

    codeIdPhoneType: 'HOME',

    extension: null,

  }

}



function applyDefaultGuestPhoneCountry() {

  const code = eventLocationAddressCountryCode.value

  if (!code) return

  // Only seed the default when the user hasn't entered a number yet

  if (!guestPhoneModel.value || !guestPhoneModel.value.number) {

    guestPhoneModel.value = newGuestPhoneModel()

  }

}



function loadGuestPhoneCountries() {

  GetCountriesFromLocalStorageOrApi().then(data => {

    if (data?.items) {

      const list: IGuestPhoneCountry[] = []

      for (const country of data.items) {

        list.push({ text: country.countryCode, id: country.countryCode })

      }

      list.sort((a, b) => a.text.localeCompare(b.text))

      guestPhoneCountries.value = list

      applyDefaultGuestPhoneCountry()

    }

  })

}



// Individual validation functions following CCMAContactDetails pattern

const validateFirstName = (): boolean => {

  const nameRegex = /^[\p{L}\s'-]+$/u



  if (!formInitialValues.value.firstName?.trim()) {

    errors.firstName = t('consultantCorner.contactDetails.FirstNameRequired')

    return false

  } else if (!nameRegex.test(formInitialValues.value.firstName)) {

    errors.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })

    return false

  } else if (formInitialValues.value.firstName.trim().length < 2) {

    errors.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })

    return false

  } else if (formInitialValues.value.firstName.length > 50) {

    errors.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })

    return false

  }



  errors.firstName = ''

  return true

}



const validateLastName = (): boolean => {

  const nameRegex = /^[\p{L}\s'-]+$/u



  if (!formInitialValues.value.lastName?.trim()) {

    errors.lastName = t('consultantCorner.contactDetails.LastNameRequired')

    return false

  } else if (!nameRegex.test(formInitialValues.value.lastName)) {

    errors.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })

    return false

  } else if (formInitialValues.value.lastName.trim().length < 2) {

    errors.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })

    return false

  } else if (formInitialValues.value.lastName.length > 50) {

    errors.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })

    return false

  }



  errors.lastName = ''

  return true

}



const validateEmailAddress = (): boolean => {

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/



  if (!formInitialValues.value.emailAddress?.trim()) {

    errors.emailAddress = t('consultantCorner.contactDetails.EmailRequired')

    return false

  } else if (!emailRegex.test(formInitialValues.value.emailAddress)) {

    errors.emailAddress = t('consultantCorner.contactDetails.EmailInvalid')

    return false

  } else if (isConsultantOwnEmail(formInitialValues.value.emailAddress)) {

    errors.emailAddress = t('validators.consultantOwnEmailNotAllowed')

    return false

  }



  errors.emailAddress = ''

  return true

}



const validateForm = (): boolean => {

  // Clear previous errors

  Object.keys(errors).forEach(key => {

    errors[key as keyof typeof errors] = ''

  })



  let isValid = true



  // Validate individual fields

  if (!validateFirstName()) isValid = false

  if (!validateLastName()) isValid = false

  if (!validateEmailAddress()) isValid = false



  // Validate phone if number entered

  if (guestPhoneModel.value?.number && guestPhoneControl.value) {

    if (!guestPhoneControl.value.validate()) {

      isValid = false

    }

  }



  return isValid

}



const statuses = ref<ICategoryCode[]>([])

const panelBarItems = ref([

  {

    expanded: true,

    title: t('eventManagement.EventInfo'),

    content: 'first',

  },

  {

    expanded: true,

    title: t('eventManagement.GuestInfo'),

    content: 'second',

  },

])



/** Collapse then re-expand panels so Kendo recalculates max-height after content changes.

 *  Uses a brief delay to allow child components (e.g. AddressControl) to finish their

 *  own async data loading before Kendo measures the final content height. */

function refreshFirstPanelBarHeight() {

  return new Promise<void>(resolve => {

    setTimeout(async () => {

      panelBarItems.value = panelBarItems.value.map(item => ({ ...item, expanded: false }))

      await nextTick()

      panelBarItems.value = panelBarItems.value.map(item => ({ ...item, expanded: true }))

      resolve()

    }, 300)

  })

}

const selectedField = ref<string>('selected')

const loader = ref<boolean>(false)

const gridData = ref<IEventGuest[]>([])

const columns = [

  {

    field: 'name',

    title: t('eventManagement.GuestName'),

    width: resolveColumnWidth('180px', t('eventManagement.GuestName')),

  },

  {

    field: 'role',

    title: t('eventManagement.GuestRole'),

    width: resolveColumnWidth('120px', t('eventManagement.GuestRole')),

    cell: roleCellFunction,

    className: 'guest-role-cell',

  },

  {

    field: 'guestSendEmail',

    title: t('eventManagement.SendEmail'),

    width: resolveColumnWidth('175px', t('eventManagement.SendEmail')),

    cell: 'guestSendEmailTemplate',

  },

  {

    field: 'guestRSVP',

    title: t('eventManagement.RSVP'),

    width: resolveColumnWidth('70px', t('eventManagement.RSVP')),

  },

  {

    field: 'guestOrderTotal',

    title: t('eventManagement.OrderTotal'),

    width: resolveColumnWidth('125px', t('eventManagement.OrderTotal')),

    cell: 'contactTotalTemplate',

  },

  {

    field: 'guestInviteOpened',

    title: t('eventManagement.InviteOpened'),

    width: resolveColumnWidth('125px', t('eventManagement.InviteOpened')),

  },

  {

    field: 'guestInviteVisited',

    title: t('eventManagement.InviteVisited'),

    width: resolveColumnWidth('125px', t('eventManagement.InviteVisited')),

  },

  {

    field: 'guestPlaceOrder',

    title: t('eventManagement.CreateOrder'),

    width: resolveColumnWidth('140px', t('eventManagement.CreateOrder')),

    headerClassName: 'guest-order-grid-header',

    cell: 'contactPlaceOrderTemplate',

  },

  {

    field: 'guestReceipt',

    title: ' ',

    headerClassName: 'guest-grid-receipt-header',

    cell: 'contactReceiptTemplate',

    width: 60,

  },

  {

    field: 'guestDelete',

    title: t('eventManagement.Delete'),

    headerClassName: 'guest-grid-delete-header',

    cell: 'contactDeleteTemplate',

    width: resolveColumnWidth('60px', t('eventManagement.Delete')),

  },

]

const selectedTab = ref(0)

const addGuestTabs = [

  {

    title: t('eventManagement.NewContact'),

    content: 'AddGuestOne',

  },

  {

    title: t('eventManagement.FindContact'),

    content: 'AddGuestTwo',

  },

]

const verifyAddress = ref<IAddressVerificationListItem[]>([])

const addressVerificatioShow = ref<boolean>(false)

const addressVerifyError = ref<boolean>(false)

const partyOrderEntryURL = ref<string>('')

const cartDetailURL = ref<string>('')

const orderReceiptURL = ref<string>('')



// Watch for address verification popup to control body scrolling

watch(addressVerificatioShow, (newValue) => {

  if (newValue) {

    // Prevent body scrolling when popup is open

    document.body.style.overflow = 'hidden'

  } else {

    // Restore body scrolling when popup is closed

    document.body.style.overflow = ''

  }

})



// --- Host Portal (Step 1) ---

type HostPortalTheme = { id: string; text: string; imageUrl?: string; previewHtml?: string; emailTemplate?: string; emailSubject?: string }

const hostPortalThemes = ref<HostPortalTheme[]>([])

const selectedHostPortalTheme = ref<HostPortalTheme | null>(null)



// Load themes from new HostPortal API

async function loadHostPortalThemes() {

  try {

    const response = await GetHostPortalThemes()

    if (response.items) {

      // Filter only enabled themes and map to dropdown format

      const enabledThemes = response.items.filter((theme: IHostPortalTheme) => theme.isEnabled)

      hostPortalThemes.value = enabledThemes.map((theme: IHostPortalTheme) => ({

        id: theme.themeName,

        text: theme.themeName,

        imageUrl: theme.themeFilePath,

        emailTemplate: '',

        emailSubject: '',

      }))

      // Set first theme as default selection

      if (hostPortalThemes.value.length > 0) {

        selectedHostPortalTheme.value = hostPortalThemes.value[0]

        // Load details for the first theme

        await loadThemeDetails(hostPortalThemes.value[0].id)

      }

    }

  } catch (error) {

    logger.error('Failed to load host portal themes:', error)

    await handleError(error, t)

  }

}



// Load theme details when a theme is selected

async function loadThemeDetails(themeName: string) {

  try {

    const cultureName = await GetCultureName()

    const response = await GetHostPortalDetail(themeName, 0, cultureName)

    if (response.item) {

      // Update the selected theme with email template and subject

      const selectedTheme = hostPortalThemes.value.find(theme => theme.id === themeName)

      if (selectedTheme) {

        selectedTheme.emailTemplate = response.item.messageBody || ''

        selectedTheme.emailSubject = response.item.messageSubject || ''

        // Update parsed preview if this is the currently selected theme

        if (selectedHostPortalTheme.value?.id === themeName) {

          await updateParsedEmailPreview()

        }

      }

    }

  } catch (error) {

    logger.error('Failed to load theme details:', error)

    await handleError(error, t)

  }

}



// Watch for theme selection changes and load details

watch(selectedHostPortalTheme, async (newTheme) => {

  if (newTheme && newTheme.id) {

    await loadThemeDetails(newTheme.id)

    await updateParsedEmailPreview()

  }

})



// Load existing host portal settings

async function loadExistingHostPortalSettings() {

  try {

    const getRequest = { eventIdToken: eventIdToken.value }

    const existingEvent = await HostPortalEventGet(getRequest)



    if (existingEvent.item) {

      // Populate form fields with existing values

      const existingSettings = existingEvent.item



      // Set theme selection

      const matchingTheme = hostPortalThemes.value.find(theme => theme.id === existingSettings.themeName)

      if (matchingTheme) {

        selectedHostPortalTheme.value = matchingTheme

      }



      // Set other form fields - decode password

      hostPortalPassword.value = existingSettings.password ? atob(existingSettings.password) : ''

      hostPortalNote.value = existingSettings.additionalInviteTextHost || ''

      hostPortalCcMe.value = existingSettings.ccconsultantonallCommunication === 1

    }

  } catch (error) {

    logger.error('Failed to load existing host portal settings:', error)

    // Don't show error to user as this is optional loading

  }

}



// --- End Host Portal (Step 1) ---



// --- Host Portal Step 2/3 state and handlers ---

const hostPortalPassword = ref<string>('')

const hostPortalNote = ref<string>('')

const hostPortalCcMe = ref<boolean>(false)



// True when host portal has been configured and the event is still open — same

// condition that enables the "View Host Dashboard" button. Drives visibility of

// the per-row "Host Portal" column in the guest grid.

const hostPortalAccessAvailable = computed(

  () => !!hostPortalPassword.value && savedEventStatus.value !== 'C'

)



// Grid columns with the optional "Host Portal" column inserted after the order

// total column when host-portal access is available.

const gridColumns = computed(() => {

  if (!hostPortalAccessAvailable.value) return columns

  const hostPortalColumn = {

    field: 'hostPortalAccess',

    title: t('eventManagement.HostPortalColumn'),

    width: '140px',

    cell: 'hostPortalAccessTemplate',

  }

  const insertAfter = columns.findIndex(c => c.field === 'guestOrderTotal')

  const insertIndex = insertAfter >= 0 ? insertAfter + 1 : columns.length

  return [

    ...columns.slice(0, insertIndex),

    hostPortalColumn,

    ...columns.slice(insertIndex),

  ]

})



// --- Email editing mode state ---

const showEmailEditor = ref<boolean>(false)

const emailTo = ref<string>('')

const emailFrom = ref<string>('')

const emailRegarding = ref<string>('')



async function saveHostPortalSettings(showSuccessMessage = true) {

  try {

    SetIsLoading(true)



    // First, try to get existing host portal event

    const getRequest = { eventIdToken: eventIdToken.value }

    const existingEvent = await HostPortalEventGet(getRequest)



    if (existingEvent.item && existingEvent.item.sfaHostPortalEventGuid > 0) {

      // Update existing host portal event

      const updateRequest = {

        eventIdToken: eventIdToken.value,

        themeName: selectedHostPortalTheme.value?.id || '',

        password: btoa(hostPortalPassword.value),

        additionalInviteTextHost: hostPortalNote.value,

        ccconsultantonallCommunication: hostPortalCcMe.value ? 1 : 0

      }



      await HostPortalEventUpdate(updateRequest)

    } else {

      // Create new host portal event

      const createRequest = {

        eventIdToken: eventIdToken.value,

        themeName: selectedHostPortalTheme.value?.id || '',

        password: btoa(hostPortalPassword.value),

        additionalInviteTextHost: hostPortalNote.value,

        ccconsultantonallCommunication: hostPortalCcMe.value ? 1 : 0

      }



      await HostPortalEventCreate(createRequest)

    }



    SetIsLoading(false)

    if (showSuccessMessage) {

      Swal.fire({

        title: t('eventManagement.HostPortalHeader'),

        text: t('eventManagement.PortalSettingsSaved'),

        icon: 'success',

      })

    }

  } catch (error) {

    SetIsLoading(false)

    logger.error('Failed to save host portal settings:', error)

    await handleError(error, t)

  }

}



async function sendHostPortalInvitation() {

  try {

    // Save host portal settings first

    await saveHostPortalSettings(false)



    // Set the email recipient to the host's email address

    emailTo.value = eventHostEmail.value || ''

    // Set email subject from selected theme and parse it

    const selectedTheme = hostPortalThemes.value.find(theme => theme.id === selectedHostPortalTheme.value?.id)

    if (selectedTheme) {

      emailRegarding.value = await parseEmailTemplate(selectedTheme.emailSubject || '')

    }

    // Update the email preview with current password value

    await updateParsedEmailPreview()

    // Show email editor instead of sending immediately

    showEmailEditor.value = true

  } catch (error) {

    logger.error('Failed to save host portal settings before sending invitation:', error)

    await handleError(error, t)

  }

}



function cancelEmailEditor() {

  showEmailEditor.value = false

}



async function sendEmailInvitation() {

  try {

    // Validate required fields

    if (!emailTo.value || !emailFrom.value || !emailRegarding.value) {

      await Swal.fire({

        icon: 'warning',

        title: t('alerts.ValidationError'),

        text: t('eventManagement.PleaseCompleteAllFields'),

      })

      return

    }



    // Prepare email request

    const emailRequest = {

      fromEmailAddress: emailFrom.value,

      toEmailAddress: emailTo.value,

      emailSubject: emailRegarding.value,

      emailBody: parsedEmailPreview.value,

    }



    // Send the email via API

    const response = await SendEmail(emailRequest)



    if (response.success) {

      await Swal.fire({

        icon: 'success',

        title: t('eventManagement.HostPortalHeader'),

        text: t('eventManagement.InvitationEmailSent'),

      })

      showEmailEditor.value = false

    } else {

      await Swal.fire({

        icon: 'error',

        title: t('alerts.Error'),

        text: t('eventManagement.EmailSendFailed'),

      })

    }

  } catch (error) {

    logger.error('Failed to send host portal invitation email:', error)

    await handleError(error, t)

  }

}



// Parse email template monikers with actual data

async function parseEmailTemplate(template: string): Promise<string> {

  if (!template) return ''



  // Get consultant information

  let consultantName = ''

  let consultantEmail = emailFrom.value

  let consultantPhone = ''



  try {

    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (beeNumber) {

      const consultant = await GetConsultantFromLocalStorageOrApi(beeNumber)

      if (consultant) {

        consultantName = `${consultant.firstName} ${consultant.lastName}`

        consultantEmail = consultant.emailAddress

        consultantPhone = consultant.phone

        // Update emailFrom with consultant's email

        emailFrom.value = consultant.emailAddress

      }

    }

  } catch (error) {

    logger.error('Failed to get consultant information:', error)

  }



  // Debug logging for password

  logger.info('parseEmailTemplate - hostPortalPassword.value:', hostPortalPassword.value)

  logger.info('parseEmailTemplate - password length:', hostPortalPassword.value?.length)

  logger.info('parseEmailTemplate - template contains {HostPortalPassword}:', template.includes('{HostPortalPassword}'))



  // Log all monikers found in template

  const monikerMatches = template.match(/\{[^}]+\}/g) || []

  logger.info('All monikers found in template:', monikerMatches)



  const monikerMap: Record<string, string> = {

    '{HostPortalHostName}': eventHostName.value,

    '{EventLocation}': eventLocationAddressLine1.value,

    '{EventDateTime}': `${formattedEventTime.value} on ${formattedEventDate.value}`,

    '{HostPortalPassword}': hostPortalPassword.value || '[Password not set]',

    '{HostPortalLink}': siteHomeofficeUrl.value + hostDashboardURL.value + eventIdToken.value,

    '{ConsultantName}': consultantName,

    '{ConsultantFullName}': consultantName,

    '{ConsultantEmail}': consultantEmail,

    '{ConsultantPhone}': consultantPhone,

  }



  let parsedTemplate = template

  Object.entries(monikerMap).forEach(([moniker, value]) => {

    const beforeCount = (parsedTemplate.match(new RegExp(moniker, 'g')) || []).length

    parsedTemplate = parsedTemplate.replace(new RegExp(moniker, 'g'), value)

    const afterCount = (parsedTemplate.match(new RegExp(moniker, 'g')) || []).length

    if (moniker === '{HostPortalPassword}') {

      logger.info(`Replaced ${beforeCount} instances of ${moniker} with "${value}"`)

      logger.info('After replacement, remaining instances:', afterCount)

    }

  })



  return parsedTemplate

}



const parsedEmailPreview = ref('')



// Update parsed email preview when theme changes

async function updateParsedEmailPreview() {

  const template = selectedHostPortalTheme.value?.emailTemplate

  logger.info('updateParsedEmailPreview - template length:', template?.length)

  logger.info('updateParsedEmailPreview - template substring (first 500 chars):', template?.substring(0, 500))

  if (template) {

    parsedEmailPreview.value = await parseEmailTemplate(template)

  } else {

    parsedEmailPreview.value = t('eventManagement.NoEmailTemplate')

  }

}



const formattedEventDate = computed(() =>

  txtEventDate.value ? txtEventDate.value.toLocaleDateString() : ''

)

const formattedEventTime = computed(() =>

  txtEventStartTime.value ? txtEventStartTime.value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''

)



onMounted(async () => {

  logger.debug('onMounted')

  await loadConsultantEmailAddress()



  GetHostDashboardUrl().then(url => {

    hostDashboardURL.value = url + '?event='

  })



  GetHostPortalUrl().then(url => {

    hostPortalURL.value = url

  })



  GetSiteHomeofficeUrl().then(url => {

    siteHomeofficeUrl.value = url

  })



  partyOrderEntryURL.value = await GetPartyOrderEntryUrl()

  cartDetailURL.value = await GetShopUrl()

  orderReceiptURL.value = await GetOrderReceiptUrl()



  SetIsLoading(true)

  GetCodesFromLocalStorageOrApi('SFAEVENTSTATUS').then(data => {

    if (data?.items) {

      for (const code of data?.items) {

        statuses.value.push({ text: code.codeText, id: code.codeId })

      }

      statuses.value.sort((a: ICategoryCode, b: ICategoryCode) => {

        return a.text.localeCompare(b.text)

      })

    }

  })



  eventListURL.value = await GetEventListUrl()



  loadGuestPhoneCountries()



  const urlParams = new URLSearchParams(window.location.search)

  const qsEventId = urlParams.get('event')



  beeNumberAuth.value = await GetBeeNumberAuthenicatedUser()



  if (

    beeNumberAuth.value !== null &&

    beeNumberAuth.value !== 0 &&

    qsEventId !== null

  ) {

    EventGet({

      beeNumber: beeNumberAuth.value,

      eventIdToken: qsEventId,

    }).then(async eventResponse => {

      if (eventResponse?.item) {

        const event = eventResponse.item

        eventTitle.value = event.title

        eventID.value = event.eventId

        eventIdToken.value = event.eventIdToken

        eventType.value = event.eventType

        selectedEventStatus.value = {

          text: event.codeIdEventStatus.localizedValue,

          id: event.codeIdEventStatus.codeId,

        }

        savedEventStatus.value = event.codeIdEventStatus.codeId

        txtEventDate.value = new Date(event.eventStartDate)

        txtEventStartTime.value = new Date(event.eventStartDate)

        txtEventEndTime.value = new Date(event.eventEndDate)

        // Store original date/time values

        txtEventDateOrig.value = new Date(event.eventStartDate)

        txtEventStartTimeOrig.value = new Date(event.eventStartDate)

        txtEventEndTimeOrig.value = new Date(event.eventEndDate)

        eventLocationAddressId.value = event.address.addressId

        eventLocationAddressLine1.value = event.address.addressLine1

        eventLocationAddressLine2.value = event.address.addressLine2

        eventLocationAddressHouseNumber.value = event.address.houseNumber

        eventLocationAddressCity.value = event.address.city

        eventLocationAddressStateCode.value = event.address.stateCode

        eventLocationAddressPostalCode.value = event.address.postalCode

        eventLocationAddressCountryCode.value = event.address.countryCode

        eventLocationAddressFirstName.value = event.address.firstName ?? null

        eventLocationAddressLastName.value = event.address.lastName ?? null

        applyDefaultGuestPhoneCountry()

        logger.debug('Address values set:', {

          addressLine1: eventLocationAddressLine1.value,

          city: eventLocationAddressCity.value,

          stateCode: eventLocationAddressStateCode.value,

          postalCode: eventLocationAddressPostalCode.value,

          countryCode: eventLocationAddressCountryCode.value

        })

        eventLocationAddressIdOrig.value = event.address.addressId

        eventLocationAddressLine1Orig.value = event.address.addressLine1

        eventLocationAddressLine2Orig.value = event.address.addressLine2

        eventLocationAddressHouseNumberOrig.value = event.address.houseNumber

        eventLocationAddressCityOrig.value = event.address.city

        eventLocationAddressStateCodeOrig.value = event.address.stateCode

        eventLocationAddressPostalCodeOrig.value = event.address.postalCode

        eventLocationAddressCountryCodeOrig.value = event.address.countryCode

        eventHostName.value = event.hostName



        await loadHostPortalThemes()

        await loadExistingHostPortalSettings()

        await nextTick()

        await refreshFirstPanelBarHeight()

        SetIsLoading(false)

      }

    })



    loader.value = true

    LoadGuestList(qsEventId)

  }



  // Add keyboard event listener for popup

  document.addEventListener('keydown', handleKeydown)

})



onUnmounted(() => {

  // Clean up event listener and restore body scroll if component unmounts while popup is open

  document.removeEventListener('keydown', handleKeydown)

  if (showSendEmailPopup.value) {

    document.body.style.overflow = ''

  }

})



async function LoadGuestList(eventIdToken: string) {

  logger.debug('LoadGuestList')



  try {

    const guests = await EventGuestGetList({

      eventIdToken: eventIdToken,

    })



    if (guests) {

      // Process guests and add computed properties

      for (const guest of guests.items) {

        if (guest.codeIdEventRole.codeId == 'H') {

          eventHostName.value = guest.firstName + ' ' + guest.lastName

          eventHostFirstName.value = guest.firstName

          eventHostLastName.value = guest.lastName

          eventHostEmail.value = guest.email

          eventHostPhoneNumber.value = guest.phoneNumber.formattedNumber

        }



        guest.name = guest.firstName + ' ' + guest.lastName

        guest.role = guest.codeIdEventRole.localizedValue

        guest.roleId = guest.codeIdEventRole.codeId



        if (guest.orderId != null && guest.orderId > 0) {

          canBeDeleted.value = false

        }

      }



      // Sort guests: Host first, then others alphabetically by name

      const sortedGuests = guests.items.sort((a, b) => {

        // Host (role 'H') always comes first

        if (a.codeIdEventRole.codeId === 'H' && b.codeIdEventRole.codeId !== 'H') {

          return -1

        }

        if (a.codeIdEventRole.codeId !== 'H' && b.codeIdEventRole.codeId === 'H') {

          return 1

        }

        // For non-host guests, sort alphabetically by name

        return a.name.localeCompare(b.name)

      })



      // Add sorted guests to grid

      gridData.value = sortedGuests



      // Also check for unsubmitted carts with items

      try {

        const masterOrderResponse = await EventMasterOrderGet({

          eventIdToken: eventIdToken,

        })



        if (masterOrderResponse?.item?.carts) {

          const cartsWithUnsubmittedItems = masterOrderResponse.item.carts.filter((cart: IAoeCart) =>

            cart.items && cart.items.length > 0 && !cart.invoice

          )



          if (cartsWithUnsubmittedItems.length > 0) {

            canBeDeleted.value = false

          }

        }

      } catch (error) {

        logger.error('Error checking carts in LoadGuestList:', error)

        // On error, don't change canBeDeleted value

      }



      loader.value = false

    }

  } catch (error) {

    logger.error('Error loading guest list:', error)

    loader.value = false

  }

}



function onSelectHandler(event: TabStripSelectEventArguments) {

  // Prevent tab selection if event status is 'C' (closed/cancelled)

  if (savedEventStatus.value === 'C') {

    return;

  }

  selectedTab.value = event.selected

}



async function statusChangeHandler(value: ICategoryCode) {

  logger.debug('statusChangeHandler: ' + value)



  // If trying to close the event (status 'C'), check for open carts with items

  if (value.id === 'C') {

    try {

      const masterOrderResponse = await EventMasterOrderGet({

        eventIdToken: eventIdToken.value,

      })



      if (masterOrderResponse?.item?.carts) {

        // Check if any cart has items AND hasn't been submitted (no invoice)

        const cartsWithUnsubmittedItems = masterOrderResponse.item.carts.filter((cart: IAoeCart) =>

          cart.items && cart.items.length > 0 && !cart.invoice

        )



        if (cartsWithUnsubmittedItems.length > 0) {

          // Show error dialog and prevent status change

          await Swal.fire({

            title: t('eventManagement.CannotCloseEventWithItemsTitle'),

            text: t('eventManagement.CannotCloseEventWithItemsMessage'),

            icon: 'warning',

            confirmButtonText: t('buttons.Okay'),

          })

          return // Don't change the status

        }

      }

    } catch (error) {

      logger.error('Error checking carts before closing event:', error)

      // On error, allow the status change to proceed (fail open)

    }

  }



  selectedEventStatus.value = value

}



function dateChangeHandler(event: DatePickerChangeEvent) {

  logger.debug('dateChangeHandler event:', event)

  if (event.value != null) {

    txtEventDate.value = event.value

    txtEventStartTime.value = new Date(

      txtEventDate.value.getFullYear(),

      txtEventDate.value.getMonth(),

      txtEventDate.value.getDate(),

      txtEventStartTime.value?.getHours(),

      txtEventStartTime.value?.getMinutes(),

    )

    txtEventEndTime.value = new Date(

      txtEventDate.value.getFullYear(),

      txtEventDate.value.getMonth(),

      txtEventDate.value.getDate(),

      txtEventEndTime.value?.getHours(),

      txtEventEndTime.value?.getMinutes(),

    )

  }

}



function startTimeChangeHandler(event: { value: Date | null }) {

  logger.debug('startTimeChangeHandler event:', event)

  if (event.value != null && txtEventDate.value != null) {

    txtEventStartTime.value = new Date(

      txtEventDate.value.getFullYear(),

      txtEventDate.value.getMonth(),

      txtEventDate.value.getDate(),

      event.value.getHours(),

      event.value.getMinutes(),

    )

    // Allow any start time to be set - validation will happen on save

  }

}



function validateEmailFormat(email: string): boolean {

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return emailRegex.test(email)

}



function validateEventDateTime(): boolean {

  // Check if date or time has changed

  const dateChanged = txtEventDate.value?.getTime() !== txtEventDateOrig.value?.getTime()

  const startTimeChanged = txtEventStartTime.value?.getTime() !== txtEventStartTimeOrig.value?.getTime()

  const endTimeChanged = txtEventEndTime.value?.getTime() !== txtEventEndTimeOrig.value?.getTime()



  // Only validate if date or time has changed

  if (!dateChanged && !startTimeChanged && !endTimeChanged) {

    return true // No changes, skip validation

  }



  // Validate that the event date is not in the past (only if date changed)

  if (dateChanged && txtEventDate.value) {

    const selectedDate = new Date(txtEventDate.value)

    const today = new Date()

    today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison

    selectedDate.setHours(0, 0, 0, 0)



    if (selectedDate < today) {

      Swal.fire({

        title: t('alerts.ValidationError'),

        text: t('eventManagement.EventDatePastError'),

        icon: 'error',

        confirmButtonText: t('buttons.Okay'),

      })

      return false

    }

  }
  
  return true

}



function endTimeChangeHandler(event: { value: Date | null }) {

  logger.debug('endTimeChangeHandler event:', event)

  if (event.value != null && txtEventDate.value != null) {

    txtEventEndTime.value = new Date(

      txtEventDate.value.getFullYear(),

      txtEventDate.value.getMonth(),

      txtEventDate.value.getDate(),

      event.value.getHours(),

      event.value.getMinutes(),

    )

    // Validation will happen on save

  }

}



function addressControlUpdateHandler(data: IAddress) {

  logger.debug('addressControlUpdateHandler', data)

  eventLocationAddressLine1.value = data.addressLine1

  eventLocationAddressLine2.value = data.addressLine2

  eventLocationAddressHouseNumber.value = data.houseNumber

  eventLocationAddressCity.value = data.city

  eventLocationAddressStateCode.value = data.stateCode

  eventLocationAddressPostalCode.value = data.postalCode

  eventLocationAddressCountryCode.value = data.countryCode

}



function onGuestFormSubmitClickHandler(event: Event) {

  event.preventDefault()

  logger.debug('onGuestFormSubmitClickHandler')



  // Manual validation following CCMAContactDetails pattern

  if (!validateForm()) {

    logger.debug('Guest form validation failed')

    return

  }



  const formValues = formInitialValues.value

  const guestPhoneValue = guestPhoneModel.value?.number?.trim() ?? ''



  let phoneNumber = {

    number: null,

    countryCode: null,

    codeIdPhoneType: null,

  } as IPhoneInput

  if (guestPhoneValue.length > 0) {

    phoneNumber = {

      number: guestPhoneValue,

      countryCode: guestPhoneModel.value?.countryCode || eventLocationAddressCountryCode.value,

      codeIdPhoneType: 'HOME',

    } as IPhoneInput

  }



  HostPortalAddressBookCreate({

    firstName: formValues.firstName,

    lastName: formValues.lastName,

    emailAddress: formValues.emailAddress,

    primaryPhone: phoneNumber,

    addressIdShipping: null,

    eventIdToken: eventIdToken.value,

  })

    .then(addressBookResp => {

      logger.debug('AddressBookCreate', addressBookResp)



      if (addressBookResp) {

        const addressBookId = addressBookResp.addressBookId

        if (beeNumberAuth.value != null) {

          SetIsLoading(true)

          EventGuestCreate({

            codeIdEventRole: 'G',

            eventIdToken: eventIdToken.value,

            guestBeeNumber: null,

            addressBookId: addressBookId,

          }).then(eventGuest => {

            SetIsLoading(false)

            if (eventGuest) {

              // Reset form

              formInitialValues.value = { } as IAoeCustomer

              guestPhoneModel.value = newGuestPhoneModel()

              // Clear errors

              errors.firstName = ''

              errors.lastName = ''

              errors.emailAddress = ''

              // Trigger master order creation/refresh to assign customerBeeNumber

              EventMasterOrderGet({

                eventIdToken: eventIdToken.value,

              }).then(() => {

                // After master order is processed, refresh the guest list

                refreshGuestGrid()

              }).catch(error => {

                logger.error('Failed to refresh master order:', error)

                // Still refresh guest list even if master order fails

                refreshGuestGrid()

              })

            }

          })

        }

      }

    })

    .catch(error => {

      SetIsLoading(false)

      handleError(error, t)

    })

}



function refreshGuestGrid() {

  logger.debug('refreshGuestGrid')

  selectedTab.value = 0

  loader.value = true

  gridData.value = []

  LoadGuestList(eventIdToken.value)

}



function saveEventHandler(event: MouseEvent) {

  logger.debug('saveEventHandler', event)



  // Validate date and time before saving

  if (!validateEventDateTime()) {

    return // Don't proceed with save if validation fails

  }

  // Validate AddressControl fields before saving
  if (addressControlRef.value && !addressControlRef.value.validate()) {
    return // Don't proceed with save if address validation fails
  }

  if (

    eventLocationAddressLine1.value != eventLocationAddressLine1Orig.value ||

    eventLocationAddressLine2.value != eventLocationAddressLine2Orig.value ||

    eventLocationAddressHouseNumber.value != eventLocationAddressHouseNumberOrig.value ||

    eventLocationAddressCity.value != eventLocationAddressCityOrig.value ||

    eventLocationAddressStateCode.value !=

      eventLocationAddressStateCodeOrig.value ||

    eventLocationAddressPostalCode.value !=

      eventLocationAddressPostalCodeOrig.value ||

    eventLocationAddressCountryCode.value !=

      eventLocationAddressCountryCodeOrig.value

  ) {

    eventLocationAddressId.value = 0

    addAddress(false).then(() => {

      if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {

        addressVerificatioShow.value = true

      } else {

        updateOriginalAddressValues()

        SaveEventInfo(true) // Address was changed

      }

    })

  } else {

    SaveEventInfo(false) // Address was not changed

  }

}



async function addAddress(skipAddressValidation: boolean) {

  verifyAddress.value = []

  const address: IAddress = {

    firstName: eventLocationAddressFirstName.value,

    lastName: eventLocationAddressLastName.value,

    addressLine1: eventLocationAddressLine1.value,

    addressLine2: eventLocationAddressLine2.value,

    houseNumber: eventLocationAddressHouseNumber.value,

    city: eventLocationAddressCity.value,

    stateCode: eventLocationAddressStateCode.value,

    postalCode: eventLocationAddressPostalCode.value,

    countryCode: eventLocationAddressCountryCode.value,

  }



  logger.debug('AddressAdd', address)

  await AddressAdd({

    address: address,

    skipAddressValidation: skipAddressValidation,

  }).then(response => {

    logger.debug('AddressAdd response:', response)

    for (const item of response.items) {

      verifyAddress.value.push(item)

    }



    if (response.addressValidationMessageId != undefined && response.addressValidationMessageId.length > 0) {

      addressVerifyError.value = true

    } else {

      addressVerifyError.value = false

    }



    eventLocationAddressId.value = response.addressId

  })



  if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {

    addressVerificatioShow.value = true

  } else {

    addressVerificatioShow.value = false

  }

}



async function deleteEventHandler(event: MouseEvent) {

  logger.debug('deleteEventHandler', event)



  // Check for unsubmitted carts with items before allowing deletion

  try {

    const masterOrderResponse = await EventMasterOrderGet({

      eventIdToken: eventIdToken.value,

    })



    if (masterOrderResponse?.item?.carts) {

      // Check if any cart has items AND hasn't been submitted (no invoice)

      const cartsWithUnsubmittedItems = masterOrderResponse.item.carts.filter((cart: IAoeCart) =>

        cart.items && cart.items.length > 0 && !cart.invoice

      )



      if (cartsWithUnsubmittedItems.length > 0) {

        // Show error dialog and prevent deletion

        await Swal.fire({

          title: t('eventManagement.CannotDeleteEventWithItemsTitle'),

          text: t('eventManagement.CannotDeleteEventWithItemsMessage'),

          icon: 'warning',

          confirmButtonText: t('buttons.Okay'),

        })

        return // Don't proceed with deletion

      }

    }

  } catch (error) {

    logger.error('Error checking carts before deleting event:', error)

    // On error, allow the deletion to proceed (fail open)

  }



  // If validation passes, show confirmation dialog

  Swal.fire({

    title: t('alerts.DeleteConfirmTitle'),

    text: t('alerts.DeleteConfirm', [eventTitle.value]),

    icon: 'warning',

    showCancelButton: true,

    confirmButtonText: t('buttons.Yes'),

    cancelButtonText: t('buttons.No'),

  }).then(result => {

    if (result.isConfirmed) {

      EventDelete({

        eventIdToken: eventIdToken.value,

      }).then(resp => {

        if (resp) {

          window.location.href = eventListURL.value

        }

      })

    }

  })

}



async function updateAllCartAddresses() {

  // Update shipping addresses only for carts shipping to the party address

  try {

    const masterOrderResponse = await EventMasterOrderGet({

      eventIdToken: eventIdToken.value,

    })



    if (masterOrderResponse?.item?.carts) {

      // Filter to only carts shipping to party address (PARTY or PARTY2)

      const partyShippingCarts = masterOrderResponse.item.carts.filter(

        (cart: IAoeCart) => cart.codeIdShipToType?.codeId === 'PARTY' || cart.codeIdShipToType?.codeId === 'PARTY2'

      )



      logger.debug(`Updating ${partyShippingCarts.length} carts shipping to party address (out of ${masterOrderResponse.item.carts.length} total carts)`)



      const updatePromises = partyShippingCarts.map((cart: IAoeCart) => {

        // Create shipping address from event location. The package ships to the
        // party/host location, so the name on the label should always be the
        // event host - not whichever guest happens to own this particular cart.
        // Fall back to the cart's own name only if the host hasn't loaded yet.

        const shippingAddress = {

          firstName: eventHostFirstName.value || cart.customerFirstName || cart.guestFirstName || '',

          lastName: eventHostLastName.value || cart.customerLastName || cart.guestLastName || '',

          addressLine1: eventLocationAddressLine1.value,

          addressLine2: eventLocationAddressLine2.value,

          houseNumber: eventLocationAddressHouseNumber.value,

          city: eventLocationAddressCity.value,

          stateCode: eventLocationAddressStateCode.value,

          postalCode: eventLocationAddressPostalCode.value,

          countryCode: eventLocationAddressCountryCode.value,

        }



        // Update cart shipping address

        return CartShippingAddressCreate({

          aoeCartIdToken: cart.aoeCartIdToken,

          address: shippingAddress,

          skipAddressValidation: true,

          addressId: eventLocationAddressId.value || undefined,

        }).catch(error => {

          logger.error('Failed to update cart address:', cart.aoeCartIdToken, error)

          // Don't throw - continue updating other carts

        })

      })



      await Promise.all(updatePromises)

      logger.debug('Party shipping cart addresses updated successfully')

    }

  } catch (error) {

    logger.error('Failed to update cart addresses:', error)

    // Don't throw - allow event save to continue

  }

}



function SaveEventInfo(addressChanged: boolean = false) {

  let eventStart = new Date()

  if (txtEventStartTime.value != undefined) {

    eventStart = txtEventStartTime.value

  }



  let eventEnd = new Date()

  if (txtEventEndTime.value != undefined) {

    eventEnd = txtEventEndTime.value

  }



  SetIsLoading(true)

  EventUpdate({

    eventIdToken: eventIdToken.value,

    codeIdEventStatus: selectedEventStatus.value.id,

    eventStartDate: formatDateTimeForJSONRequest(eventStart),

    eventEndDate: formatDateTimeForJSONRequest(eventEnd),

    addressId:

      eventLocationAddressId.value == 0 ? null : eventLocationAddressId.value,

    title: eventTitle.value,

  }).then(async event => {

    logger.debug('EventUpdate', event)



    // Update all cart addresses with the new event location only if address changed

    if (addressChanged) {

      logger.debug('Address changed, updating all cart addresses')

      await updateAllCartAddresses()

    }



    // Update saved status after successful save

    savedEventStatus.value = selectedEventStatus.value.id



    // Update original date/time values after successful save

    if (txtEventDate.value) txtEventDateOrig.value = new Date(txtEventDate.value)

    if (txtEventStartTime.value) txtEventStartTimeOrig.value = new Date(txtEventStartTime.value)

    if (txtEventEndTime.value) txtEventEndTimeOrig.value = new Date(txtEventEndTime.value)



    SetIsLoading(false)

    Swal.fire({

      title: t('alerts.SaveConfirmTitle'),

      text: t('alerts.SaveConfirm', [eventTitle.value]),

      icon: 'success',

      confirmButtonText: t('buttons.Okay'),

    })

  })

}



function updateOriginalAddressValues() {

  // Update original values to match current values after successful address update

  eventLocationAddressLine1Orig.value = eventLocationAddressLine1.value

  eventLocationAddressLine2Orig.value = eventLocationAddressLine2.value

  eventLocationAddressHouseNumberOrig.value = eventLocationAddressHouseNumber.value

  eventLocationAddressCityOrig.value = eventLocationAddressCity.value

  eventLocationAddressStateCodeOrig.value = eventLocationAddressStateCode.value

  eventLocationAddressPostalCodeOrig.value = eventLocationAddressPostalCode.value

  eventLocationAddressCountryCodeOrig.value = eventLocationAddressCountryCode.value

  eventLocationAddressIdOrig.value = eventLocationAddressId.value

}



function useEnteredAddressClick() {

  logger.debug('useEnteredAddressClick')



  addAddress(true).then(() => {

    updateOriginalAddressValues()

    SaveEventInfo(true) // Address was changed (user entered address)

  })

}



function useSelectedAddressClick(selectedAddress: IAddress) {

  logger.debug('useSelectedAddressClick', selectedAddress)



  eventLocationAddressLine1.value = selectedAddress.addressLine1

  eventLocationAddressLine2.value = selectedAddress.addressLine2

  eventLocationAddressHouseNumber.value = selectedAddress.houseNumber

  eventLocationAddressCity.value = selectedAddress.city

  eventLocationAddressStateCode.value = selectedAddress.stateCode

  eventLocationAddressPostalCode.value = selectedAddress.postalCode

  eventLocationAddressCountryCode.value = selectedAddress.countryCode



  addAddress(false).then(() => {

    if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {

      addressVerificatioShow.value = true

    } else {

      updateOriginalAddressValues()

      SaveEventInfo(true) // Address was changed (user selected address)

    }

  })

}



function placeOrderHandler(

  event: MouseEvent,

  customerBeeNumber: number | null,

) {

  logger.debug('placeOrderHandler', event, customerBeeNumber)

  let url = partyOrderEntryURL.value + '?event=' + eventIdToken.value

  if (customerBeeNumber != null) {

    url = url + '&customer=' + customerBeeNumber

  }

  window.location.href = url

}



function viewGuestReceipt(event: MouseEvent, aoeOrderIdToken: string) {

  logger.debug('viewGuestReceipt', aoeOrderIdToken)

  const receiptURL = orderReceiptURL.value + '?id=' + aoeOrderIdToken

  window.location.href = receiptURL

}



function deleteEventGuest(event: MouseEvent, guest: IEventGuest) {

  logger.debug('deleteEventGuest', event, guest)

  Swal.fire({

    title: t('alerts.DeleteConfirmTitle'),

    text: t('alerts.DeleteConfirm', [guest.firstName + ' ' + guest.lastName]),

    icon: 'warning',

    showCancelButton: true,

    confirmButtonText: t('buttons.Yes'),

    cancelButtonText: t('buttons.No'),

  }).then(result => {

    SetIsLoading(true)

    if (result.isConfirmed) {

      EventGuestDelete({

        eventIdToken: eventIdToken.value,

        eventContactId: guest.eventContactId,

      })

        .then(resp => {

          if (resp) {

            SetIsLoading(false)

            refreshGuestGrid()

          }

        })

        .catch(async error => {

          SetIsLoading(false)

          await handleError(error, t)

        })

    }

  })

}



function roleCellFunction(

  h: typeof import('vue').h,

  tdElement: string | object,

  props: GridCellProps,

) {

  const fieldValue = props.dataItem[props.field]

  const roleId = props.dataItem['roleId']

  logger.debug('fieldValue: ', fieldValue, ' roleId: ', roleId)

  if (roleId == 'H') {

    return h(tdElement, h('span', { class: 'host' }, fieldValue))

  }

  // Don't show menu if event status is 'C' (closed/cancelled)

  if (savedEventStatus.value === 'C') {

    return h(tdElement, h('span', fieldValue))

  }

  return h(tdElement, [

    h(

      kMenu,

      {

        items: GetMenuItems(fieldValue),

        onSelect: () => selectHandler(props.dataItem),

      },

      fieldValue,

    ),

  ])

}



function GetMenuItems(value: string) {

  const menuItems = [

    {

      svgIcon: moreVerticalIcon,

      text: value,

      items: [

        {

          text: t('eventManagement.MakeHost'),

          id: 'MakeHost',

        },

      ],

    },

  ]



  return menuItems

}



function selectHandler(guest: IEventGuest) {

  logger.debug('selectHandler guest:', guest)



  // Prevent host change if event status is 'C' (closed/cancelled)

  if (savedEventStatus.value === 'C') {

    return;

  }



  Swal.fire({

    title: t('alerts.ChangeHostTitle'),

    text: t('alerts.ChangeHostConfirm'),

    icon: 'warning',

    showCancelButton: true,

    confirmButtonText: t('buttons.Yes'),

    cancelButtonText: t('buttons.No'),

  }).then(result => {

    if (result.isConfirmed) {

      SetIsLoading(true)

      EventGuestMakeHost({

        eventIdToken: eventIdToken.value,

        eventContactId: guest.eventContactId,

      }).then(resp => {

        if (resp) {

          SetIsLoading(false)

          refreshGuestGrid()

        }

      })

    }

  })

}



function openHostDashboard() {

  logger.debug('openHostDashboard')

  if (hostDashboardURL.value && eventIdToken.value) {

    const url = hostDashboardURL.value + eventIdToken.value

    window.open(url, '_blank')

  }

}



// Opens the public-facing Host Portal page in a new tab, scoped to a specific

// guest via eventIdToken + eventContactId. Used by the per-row Host Portal button

// in the guest grid for non-host rows.

function openHostPortalForGuest(guest: IEventGuest) {

  if (!hostPortalURL.value || !eventIdToken.value || !guest?.eventContactId) {

    logger.warn('openHostPortalForGuest: missing url, token, or contact id', {

      hasUrl: !!hostPortalURL.value,

      hasToken: !!eventIdToken.value,

      contactId: guest?.eventContactId,

    })

    return

  }

  const url =

    `${hostPortalURL.value}?eventIdToken=${encodeURIComponent(eventIdToken.value)}` +

    `&eventContactId=${encodeURIComponent(String(guest.eventContactId))}`

  window.open(url, '_blank')

}

</script>



<template>

  <div>

    <div class="breadcrumb">

      <div>

        <a :href="eventListURL" class="eventListLink">{{

          t('eventManagement.EventList')

        }}</a>

      </div>

      <div>&nbsp;/&nbsp;</div>

      <div>{{ t('eventManagement.BreadCrumbPage') }}</div>

    </div>

    <div class="row manageButtons">

      <div class="col-12">

        <KButton

          ref="btnSave"

          id="btnSave"

          class="btnSave"

          :rounded="'large'"

          :theme-color="'checkoutContinue'"

          @click="saveEventHandler($event)"

          >{{ t('eventManagement.SaveButton') }}</KButton

        >

        <KButton

          v-if="canBeDeleted"

          ref="btnDelete"

          id="btnDelete"

          class="btnDelete"

          :rounded="'large'"

          :theme-color="'checkoutContinue'"

          @click="deleteEventHandler($event)"

          >{{ t('eventManagement.DeleteButton') }}</KButton

        >

        <!-- <KButton

          ref="btnTransfer"

          id="btnTransfer"

          class="btnTransfer"

          :rounded="'large'"

          :theme-color="'checkoutContinue'"

          >{{ t('eventManagement.TransferButton') }}</KButton

        > -->

      </div>

    </div>

    <div class="row mt-3 eventInfo">

      <div class="col-12">

        <PanelBar :items="panelBarItems" class="eventPanelBar">

          <template v-slot:first>

            <div class="eventInfoPanel mb-3">

              <div class="row">

                <div class="col-12 col-lg-3">{{ t('eventManagement.EventID') }}:</div>

                <div class="col-12 col-lg-3">

                  {{ eventID }}

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">{{ t('eventManagement.EventTitle') }}:</div>

                <div class="col-12 col-lg-3">

                  <KTextBox

                    v-model="eventTitle"

                    :rounded="'large'"

                    :theme-color="'primary'"

                    :disabled="savedEventStatus === 'C'"

                  ></KTextBox>

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">{{ t('eventManagement.EventType') }}:</div>

                <div class="col-12 col-lg-3">

                  <KTextBox

                    v-model="eventType"

                    :rounded="'large'"

                    :theme-color="'primary'"

                    :disabled="true"

                  ></KTextBox>

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">{{ t('eventManagement.EventStatus') }}:</div>

                <div class="col-12 col-lg-3">

                  <DropDownList

                    :data-items="statuses"

                    :value="selectedEventStatus"

                    :text-field="'text'"

                    :data-item-key="'id'"

                    :disabled="savedEventStatus === 'C'"

                    @change="statusChangeHandler($event.target.value)"

                  ></DropDownList>

                </div>

              </div>

              <div class="row">

                <div class="col-12 col-lg-6">

                  <div class="row mt-3 eventInfoHeader">

                    {{ t('eventManagement.EventWhen') }}

                  </div>

                  <div class="row mt-3">

                    <div class="col-12 col-lg-6">

                      {{ t('eventManagement.EventDate') }}:

                    </div>

                    <div class="col-12 col-lg-6">

                      <LocalizationProvider :language="locale">

                        <DatePicker

                          :value="txtEventDate"

                          @change="dateChangeHandler($event)"

                          :disabled="savedEventStatus === 'C'"

                          :format="dateFormat"

                          :formatPlaceholder="formatPlaceholder"

                        ></DatePicker>

                      </LocalizationProvider>

                    </div>

                  </div>

                  <div class="row mt-1">

                    <div class="col-12 col-lg-6">

                      {{ t('eventManagement.EventStartTime') }}:

                    </div>

                    <div class="col-12 col-lg-6">

                      <LocalizationProvider :language="locale">

                        <TimePicker

                          :value="txtEventStartTime"

                          :disabled="savedEventStatus === 'C'"

                          :format="timePickerFormat"

                          @change="startTimeChangeHandler($event)"

                        />

                      </LocalizationProvider>

                    </div>

                  </div>

                  <div class="row mt-1">

                    <div class="col-12 col-lg-6">

                      {{ t('eventManagement.EventEndTime') }}:

                    </div>

                    <div class="col-12 col-lg-6">

                      <LocalizationProvider :language="locale">

                        <TimePicker

                          :value="txtEventEndTime"

                          :disabled="savedEventStatus === 'C'"

                          :format="timePickerFormat"

                          @change="endTimeChangeHandler($event)"

                        />

                      </LocalizationProvider>

                    </div>

                  </div>

                </div>

                <div class="col-12 col-lg-6">

                  <div class="row mt-3 eventInfoHeader">

                    {{ t('eventManagement.EventWhere') }}

                  </div>

                  <div class="row">

                    <div class="col-12">

                      <AddressControl
                        ref="addressControlRef"

                        :countryCode="eventLocationAddressCountryCode"

                        :addressLine1="eventLocationAddressLine1"

                        :addressLine2="eventLocationAddressLine2"

                        :houseNumber="eventLocationAddressHouseNumber"

                        :city="eventLocationAddressCity"

                        :stateCode="eventLocationAddressStateCode"

                        :postalCode="eventLocationAddressPostalCode"

                        :isdisabled="savedEventStatus === 'C'"

                        :columnClass="'col-12 col-md-9'"

                        :showfullnamefield="false"

                        :name="null"

                        @address-change="addressControlUpdateHandler"

                      >

                      </AddressControl>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </template>

          <template v-slot:second>

            <div class="eventGuestPanel">

              <div class="row">

                <div class="col-12 eventInfoHeader">

                  {{ t('eventManagement.Hostess') }}

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">{{ t('eventManagement.HostName') }}:</div>

                <div class="col-12 col-lg-9">

                  {{ eventHostName }}

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">{{ t('eventManagement.HostEmail') }}:</div>

                <div class="col-12 col-lg-9">

                  {{ eventHostEmail }}

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 col-lg-3">

                  {{ t('eventManagement.HostPhoneNumber') }}:

                </div>

                <div class="col-12 col-lg-9">

                  {{ eventHostPhoneNumber }}

                </div>

              </div>

              <hr />

              <div class="row mt-3">

                <div class="col-12 eventInfoHeader">

                  {{ t('eventManagement.AddGuest') }}

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12">

                  <kTabStrip

                    :selected="selectedTab"

                    @select="onSelectHandler($event)"

                    :tabs="addGuestTabs"

                    class="tabsGuestAdd"

                  >

                    <template v-slot:AddGuestOne>

                      <form @submit.prevent="onGuestFormSubmitClickHandler($event)" id="guestForm">

                        <fieldset class="k-form-fieldset">

                          <div class="row mb-3">

                            <div class="col-12 col-lg-3 form-field">

                              <label class="form-label required">{{ t('columns.emailAddress') }}</label>

                              <input

                                v-model="formInitialValues.emailAddress"

                                type="email"

                                class="form-input"

                                :class="{ 'error': errors.emailAddress }"

                                required

                                @input="validateEmailAddress"

                                @blur="validateEmailAddress"

                                :disabled="savedEventStatus === 'C'"

                              />

                              <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>

                            </div>

                            <div class="col-12 col-lg-3 form-field">

                              <label class="form-label required">{{ t('columns.firstName') }}</label>

                              <input

                                v-model="formInitialValues.firstName"

                                type="text"

                                class="form-input"

                                :class="{ 'error': errors.firstName }"

                                required

                                @input="validateFirstName"

                                @blur="validateFirstName"

                                :disabled="savedEventStatus === 'C'"

                              />

                              <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>

                            </div>

                            <div class="col-12 col-lg-3 form-field">

                              <label class="form-label required">{{ t('columns.lastName') }}</label>

                              <input

                                v-model="formInitialValues.lastName"

                                type="text"

                                class="form-input"

                                :class="{ 'error': errors.lastName }"

                                required

                                @input="validateLastName"

                                @blur="validateLastName"

                                :disabled="savedEventStatus === 'C'"

                              />

                              <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>

                            </div>

                            <div class="col-12 col-lg-3 form-field">

                              <label class="form-label">{{ t('columns.phone') }}</label>

                              <PhoneInputControl

                                ref="guestPhoneControl"

                                class="guestPhoneField"

                                v-model="guestPhoneModel"

                                :required="false"

                                :showExtension="false"

                                :showPhoneType="false"

                                :countryList="guestPhoneCountries"

                                :disabled="savedEventStatus === 'C'"

                              />

                            </div>

                          </div>

                          <div class="row mt-3">

                            <div class="col-12 buttonAddGuest">

                              <KButton

                                ref="btnAddGuest"

                                id="btnAddGuest"

                                :rounded="'large'"

                                :theme-color="'checkoutContinue'"

                                type="submit"

                                class="btnAddGuest"

                                :disabled="savedEventStatus === 'C'"

                              >

                                {{ t('eventManagement.AddGuestButton') }}

                              </KButton>

                            </div>

                          </div>

                        </fieldset>

                      </form>

                    </template>

                    <template v-slot:AddGuestTwo>

                      <FindEventContactsControl

                        :showAddGuest="true"

                        :eventidtoken="eventIdToken"

                        :beeNumberAuth="beeNumberAuth"

                        @guest-grid-refresh="refreshGuestGrid"

                        :disabled="savedEventStatus === 'C'"

                      ></FindEventContactsControl>

                    </template>

                  </kTabStrip>

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12">

                  <hr />

                </div>

              </div>

              <div class="row mt-3 p-3 border rounded bg-light d-flex align-items-center">

                <!-- Cart URL and Copy Button Section -->

                <div class="col-12 col-lg-3">

                  <span class="me-2">{{ t('eventManagement.CartPageUrl') }}:</span>

                </div>

                <div class="col-12 col-lg-9">

                  <span class="me-2 text-monospace url-display" ref="cartUrlSpan">{{ cartPageUrl }}</span>

                  <KButton :theme-color="'primary'" size="small" @click="copyCartUrlToClipboard">{{ t('eventManagement.CopyCartUrl') }}</KButton>

                  <span v-if="cartUrlCopied" class="ms-2 text-success">{{ t('eventManagement.Copied') }}</span>

                </div>

              </div>

              <div class="row mt-3">

                <div class="col-12 eventInfoHeader">

                  {{ t('eventManagement.Guests') }}

                </div>

                <div class="col-12">

                  <Grid

                    ref="grid"

                    :data-items="gridData"

                    :resizable="true"

                    :reorderable="true"

                    :columns="gridColumns"

                    :selected-field="selectedField"

                    :loader="loader"

                    class="guestGrid"

                  >

                    <template v-slot:contactTotalTemplate="{ props }">

                      <td

                        v-if="props.rowType !== 'groupHeader'"

                        class="contactPlaceDiv"

                      >

                        {{

                          props.dataItem.orderId != null &&

                          props.dataItem.orderId > 0

                            ? FormatCurrency(props.dataItem.orderTotal, null) +

                              ' / ' +

                              FormatCurrency(0, null)

                            : props.dataItem.cartTotal != null

                              ? FormatCurrency(props.dataItem.cartTotal, null) +

                                ' / ' +

                                FormatCurrency(

                                  props.dataItem.cartBalanceDue,

                                  null,

                                )

                              : ''

                        }}

                      </td>

                    </template>

                    <template v-slot:contactPlaceOrderTemplate="{ props }">

                      <td

                        v-if="props.rowType !== 'groupHeader'"

                        class="contactPlaceDiv"

                      >

                        <KButton

                          v-if="

                            (props.dataItem.orderId == null ||

                              props.dataItem.orderId == 0) &&

                            savedEventStatus == 'O'

                          "

                          ref="btnPlaceOrder"

                          id="btnPlaceOrder"

                          class="btnPlaceOrder"

                          size="small"

                          :rounded="'large'"

                          :theme-color="'checkoutContinue'"

                          @click="

                            placeOrderHandler(

                              $event,

                              props.dataItem.customerBeeNumber,

                            )

                          "

                        >

                          {{ t('eventManagement.PlaceOrder') }}

                        </KButton>

                      </td>

                    </template>

                    <template v-slot:contactReceiptTemplate="{ props }">

                      <td

                        v-if="props.rowType !== 'groupHeader'"

                        class="grid-receipt guest-grid-receipt text-center"

                      >

                        <KButton

                          v-if="

                            props.dataItem.orderId != null &&

                            props.dataItem.orderId > 0 &&

                            props.dataItem.aoeOrderIdToken

                          "

                          :fill-mode="'clear'"

                          @click="viewGuestReceipt($event, props.dataItem.aoeOrderIdToken)"

                          :svg-icon="fileTxtIcon"

                          :theme-color="'primary'"

                          class="guest-grid-receipt-icon"

                          :title="t('eventManagement.ViewReceipt')"

                        ></KButton>

                      </td>

                    </template>

                    <template v-slot:contactDeleteTemplate="{ props }">

                      <td

                        v-if="props.rowType !== 'groupHeader'"

                        class="grid-delete guest-grid-delete text-center"

                      >

                        <KButton

                          v-if="

                            props.dataItem.codeIdEventRole.codeId != 'H' &&

                            (props.dataItem.orderId == null ||

                              props.dataItem.orderId == 0) &&

                            savedEventStatus == 'O'

                          "

                          :fill-mode="'clear'"

                          @click="deleteEventGuest($event, props.dataItem)"

                          :svg-icon="trashIcon"

                          :theme-color="'error'"

                          class="cart-item-grid-delete-icon"

                        ></KButton>

                      </td>

                    </template>

                    <template v-slot:guestSendEmailTemplate="{ props }">

                      <td v-if="props.rowType !== 'groupHeader'" class="text-center">

                        <KButton

                          :theme-color="'primary'"

                          size="small"

                          class="guestSendEmailBtn"

                          @click="

                            savedEventStatus !== 'C' &&

                              openSendEmailPopup(props.dataItem)

                          "

                        >

                          {{ t('eventManagement.SendEmail') }}

                        </KButton>

                      </td>

                    </template>

                    <template v-slot:hostPortalAccessTemplate="{ props }">

                      <td v-if="props.rowType !== 'groupHeader'" class="text-center">

                        <!-- Host row: opens the consultant-side Host Dashboard (same as the Step 3 button). -->

                        <KButton

                          v-if="props.dataItem.codeIdEventRole.codeId === 'H'"

                          :theme-color="'primary'"

                          size="small"

                          class="hostPortalAccessBtn"

                          @click="openHostDashboard"

                        >

                          {{ t('eventManagement.ManageHostPortal') }}

                        </KButton>

                        <!-- Guest row: opens the public Host Portal page scoped to this guest. -->

                        <KButton

                          v-else

                          :theme-color="'primary'"

                          size="small"

                          class="hostPortalAccessBtn"

                          @click="openHostPortalForGuest(props.dataItem)"

                        >

                          {{ t('eventManagement.ViewHostPortal') }}

                        </KButton>

                      </td>

                    </template>

                  </Grid>

                </div>

              </div>

              <!-- Host Portal Section -->

              <div v-if="!showEmailEditor" class="row mt-4">

                <div class="col-12">

                  <div class="host-portal-header">{{ t('eventManagement.HostPortalHeader') }}</div>

                </div>

              </div>

              <div v-if="!showEmailEditor" class="row host-portal">

                <div class="col-12 col-lg-4 mt-2">

                  <div class="host-portal-step">{{ t('eventManagement.HostPortalStep1') }}</div>

                  <div class="fw-bold mb-2">{{ t('eventManagement.ChooseTheme') }}</div>

                  <div class="mb-2 text-muted">{{ t('eventManagement.ImagePreview') }}</div>

                  <DropDownList

                    v-model="selectedHostPortalTheme"

                    :data-items="hostPortalThemes"

                    :text-field="'text'"

                    :data-item-key="'id'"

                  />

                  <div class="host-portal-preview mt-3">

                    <div class="preview-card border rounded">

                      <div class="preview-card-body">

                        <div class="text-muted small" v-html="selectedHostPortalTheme?.emailTemplate || t('eventManagement.NoEmailTemplate')">

                        </div>

                      </div>

                    </div>

                  </div>

                </div>

                <div class="col-12 col-lg-4 mt-2">

                  <div class="host-portal-step">{{ t('eventManagement.HostPortalStep2') }}</div>

                  <KTextBox v-model="hostPortalPassword" :rounded="'large'" :theme-color="'primary'" :placeholder="t('eventManagement.PasswordPlaceholder')" />

                  <div class="mt-3">{{ t('eventManagement.OptionalNote') }}</div>

                  <textarea v-model="hostPortalNote" class="form-control" rows="10"></textarea>

                  <div class="form-check mt-2">

                    <KCheckBox
                      id="ccOnEmails"
                      v-model="hostPortalCcMe"
                      :label="t('eventManagement.CcMeOnEmails')"
                    />

                  </div>

                </div>

                <div class="col-12 col-lg-4 mt-2">

                  <div class="host-portal-step">

                    <span>{{ t('eventManagement.HostPortalStep3') }}</span>

                  </div>

                  <div class="d-flex flex-column gap-2 mt-2">

                    <KButton :rounded="'large'" :theme-color="'checkoutContinue'" :disabled="!hostPortalPassword || savedEventStatus === 'C'" @click="sendHostPortalInvitation">{{ t('eventManagement.SendEmailButton') }}</KButton>

                    <KButton

                      :rounded="'large'"

                      :theme-color="'primary'"

                      :disabled="!hostPortalPassword || savedEventStatus === 'C'"

                      @click="openHostDashboard"

                      class="host-dashboard-button"

                    >

                      {{ t('eventManagement.ViewHostDashboard') }}

                    </KButton>

                  </div>

                </div>

              </div>

              <div v-if="!showEmailEditor" class="row mt-4">

                <div class="col-12 d-flex justify-content-center">

                  <KButton class="btnSave" :rounded="'large'" :theme-color="'checkoutContinue'" :disabled="savedEventStatus === 'C'" @click="saveHostPortalSettings(true)">

                    {{ t('eventManagement.SavePortalSettings') }}

                  </KButton>

                </div>

              </div>



              <!-- Email Editor Section -->

              <div v-if="showEmailEditor" class="row mt-4">

                <div class="col-12">

                  <div class="host-portal-header">{{ t('eventManagement.HostPortalHeader') }}</div>

                </div>

              </div>

              <div v-if="showEmailEditor" class="row mt-2">

                <div class="col-12">

                  <div class="email-editor-form">

                    <div class="row mb-3">

                      <div class="col-2"><strong>{{ t('eventManagement.To') }}</strong></div>

                      <div class="col-10">

                        <KTextBox v-model="emailTo" :rounded="'large'" :theme-color="'primary'"/>

                      </div>

                    </div>

                    <div class="row mb-3">

                      <div class="col-2"><strong>{{ t('eventManagement.From') }}</strong></div>

                      <div class="col-10">

                        <KTextBox v-model="emailFrom" :rounded="'large'" :theme-color="'primary'" :readonly="true" />

                      </div>

                    </div>

                    <div class="row mb-4">

                      <div class="col-2"><strong>{{ t('eventManagement.Regarding') }}</strong></div>

                      <div class="col-10">

                        <KTextBox v-model="emailRegarding" :rounded="'large'" :theme-color="'primary'" />

                      </div>

                    </div>

                    <div class="row mb-4">

                      <div class="col-12">

                        <div class="fw-bold mb-2">{{ t('eventManagement.EmailPreview') }}</div>

                        <div class="email-preview-container border rounded p-3" v-html="parsedEmailPreview"></div>

                      </div>

                    </div>

                    <div class="row">

                      <div class="col-12 d-flex justify-content-end gap-2">

                        <KButton :rounded="'large'" :theme-color="'secondary'" @click="cancelEmailEditor">{{ t('eventManagement.Cancel') }}</KButton>

                        <KButton :rounded="'large'" :theme-color="'checkoutContinue'" @click="sendEmailInvitation">{{ t('eventManagement.SendInvitation') }}</KButton>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </template>

        </PanelBar>

      </div>

    </div>



    <!-- Address Verification Popup -->

    <div v-if="addressVerificatioShow" class="address-verification-popup-overlay" @click="addressVerificatioShow = false"></div>

    <Popup

      :show="addressVerificatioShow"

      :popup-class="'address-val-popup-content aoe-event-management'"

    >

      <VerifyAddressPopup

        :enteredAddress="{

          addressLine1: eventLocationAddressLine1,

          addressLine2: eventLocationAddressLine2,

          houseNumber: eventLocationAddressHouseNumber,

          city: eventLocationAddressCity,

          stateCode: eventLocationAddressStateCode,

          postalCode: eventLocationAddressPostalCode,

          countryCode: eventLocationAddressCountryCode,

          firstName: eventLocationAddressFirstName,

          lastName: eventLocationAddressLastName,

        }"

        :verifyAddress="verifyAddress"

        :verifyAddressError="addressVerifyError"

        @use-entered-address="useEnteredAddressClick"

        @use-selected-address="useSelectedAddressClick"

        @re-enter-address="addressVerificatioShow = false"

      >

      </VerifyAddressPopup>

    </Popup>

    <!-- Send Email Popup -->

    <div v-if="showSendEmailPopup" class="send-email-popup-overlay" @click="closeSendEmailPopup">

      <div class="send-email-popup-content" @click.stop>

        <div class="p-4">

          <div class="mb-3"><strong>{{ t('eventManagement.SendEmailTo') }}:</strong> {{ sendEmailGuest?.name || '' }} ({{ sendEmailGuest?.email || '' }})</div>

          <div class="mb-3">

            <label class="form-label">{{ t('eventManagement.EmailSubject') }}</label>

            <input v-model="emailSubject" class="form-control" type="text" />

          </div>

          <div class="mb-3">

            <label class="form-label">{{ t('eventManagement.EmailBody') }}</label>

            <textarea v-model="emailBody" class="form-control" rows="4"></textarea>

          </div>

          <div class="d-flex justify-content-end">

            <KButton :theme-color="'secondary'" class="me-2" @click="closeSendEmailPopup">{{ t('eventManagement.Cancel') }}</KButton>

            <KButton :theme-color="'primary'" @click="sendGuestEmail">{{ t('eventManagement.Send') }}</KButton>

          </div>

        </div>

      </div>

    </div>

  </div>

</template>





<style lang="scss">

.k-textbox {

  // Remove thick black outline on mouse click (focus)

  // But keep it for keyboard navigation (focus-visible)

  &:focus:not(:focus-visible) {

    outline: none !important;

    box-shadow: 0 0 0 2px rgba(#0d6efd, 0.2) !important;

  }



  // Keyboard navigation (Tab key) - keep accessibility outline from global.scss

  // The :focus-visible rule in web2/global.scss will handle this

}



.error-input {

  /* !important required: Override Kendo's KTextBox default border to show error state */

  border-color: #dc3545 !important;



  // Error state focus: use red glow instead of black outline for mouse clicks

  &:focus:not(:focus-visible) {

    outline: none !important;

    box-shadow: 0 0 0 2px rgba(#dc3545, 0.25) !important;

  }

}



.k-form-error {

  color: #dc3545;

  @include typography-caption;

  margin-top: $spacing-1;

}



.form-field {

  display: flex;

  flex-direction: column;

}



.form-label {

  @include typography-body;

  @include font-weight-medium;

  margin-bottom: $spacing-2;

  color: #333;

}



.form-label.required::after {

  content: ' *';

  color: #dc3545;

}



.form-input {

  padding: $spacing-3;

  @include typography-body;

  border: 1px solid #ced4da;

  border-radius: $radius-sm;

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

}



.form-input:focus {

  outline: none;

  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);

}



.form-input.error {

  border-color: #dc3545;

}



.form-input.error:focus {

  border-color: #dc3545;

  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);

}



.form-field .error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: $spacing-1;
}



// Override PhoneInputControl styling in this component to match form inputs

.guestPhoneField .k-textbox {

  padding: 0.75rem !important;

  border: 1px solid #ced4da !important;

  border-radius: $radius-sm !important;

}



.guestPhoneField .k-textbox:focus {

  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3) !important;

}



.guestPhoneField .country-select {

  padding: 0.75rem !important;

  border: 1px solid #ced4da !important;

  border-radius: $radius-sm !important;

  border-right: 1px solid #ced4da !important;

}



.guestPhoneField .country-select:focus {

  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3) !important;

}



.guestPhoneField .phone-input {

  border-radius: $radius-sm !important;

  border-left: 1px solid #ced4da !important;

}



.guestPhoneField .phone-input:focus {

  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3) !important;

}



.guestPhoneField .phone-separator {

  display: none !important;

}



.eventInfoPanel {

  @include typography-small;

  padding: $spacing-6;

}



.send-email-popup-overlay {

  position: fixed;

  top: 0;

  left: 0;

  width: 100vw;

  height: 100vh;

  background: rgba(black, 0.5);

  z-index: $z-modal-backdrop;

  display: flex;

  align-items: center;

  justify-content: center;

}



.send-email-popup-content {

  position: relative !important;

  top: auto !important;

  left: auto !important;

  transform: none !important;

  z-index: $z-modal;

  min-width: 600px;

  max-width: 90vw;

  max-height: 90vh;

  background: white;

  border-radius: $radius-md;

  box-shadow: 0 4px 32px rgba(black, 0.2);

  padding: 0;

  display: flex;

  flex-direction: column;

  align-items: stretch;

  overflow: hidden;

}



.address-verification-popup-overlay {

  position: fixed;

  top: 0;

  left: 0;

  width: 100vw;

  height: 100vh;

  background: rgba(black, 0.5);

  z-index: $z-critical-backdrop;

}



.address-val-popup-content.aoe-event-management {

  min-width: 600px;

  max-width: 800px;

  max-height: 90vh;

  background: white;

  border-radius: $radius-sm;

  box-shadow: 0 2px 16px rgba(black, 0.2);

  padding: $card-padding;

  margin: $spacing-5;

  overflow: hidden;



  *:not(.k-button):not(.k-button *):not(button):not(button *) {

    color: #000;

  }

}



.send-email-popup-content *:not(.k-button):not(.k-button *) {

  color: #000 !important;

}



.send-email-popup-content .form-label {

  @include font-weight-semibold;

  color: #000 !important;

}



.send-email-popup-content .form-control {

  color: #000 !important;

  background-color: white !important;

  border: 1px solid #ced4da !important;

}



/* Mobile responsive adjustments */

@media (max-width: 768px) {

  .send-email-popup-content {

    min-width: auto;

    max-width: 95vw;

    margin: $spacing-2-5;

  }

}



/* Ensure guest form fields display in a row on desktop screens */
@media (min-width: 769px) {
  #guestForm .row {
    display: flex;
    flex-wrap: wrap;
    margin-right: -$spacing-2;
    margin-left: -$spacing-2;
  }

  #guestForm .row > .col-lg-3 {
    flex: 0 0 25%;
    max-width: 25%;
    padding-right: $spacing-2;
    padding-left: $spacing-2;
  }
}





.eventGuestPanel {

  @include typography-small;

  padding: $spacing-6;

}



.eventGuestPanel .k-column-title {

  @include typography-caption;

}



.eventGuestPanel .k-checkbox-wrap input {

  border: solid 1px black !important;

}



.eventInfoPanel .k-input-inner {

  @include typography-small;

}



.eventInfoHeader {

  @include typography-body;

  @include font-weight-bold;

}



.eventPanelBar :not(.guestGrid) .k-panelbar-item .k-link {

  @include typography-body-large;

  @include font-weight-bold;

  color: black !important;

}



.url-display {

  user-select: all;

  word-break: break-all;

}



.k-picker-solid {

  background-color: white !important;

}



.eventPanelBar

  .k-panelbar-item

  .tabsGuestAdd

  .k-tabstrip-items-wrapper

  .k-link {

  @include typography-small;

  background-color: white !important;

  color: black !important;

  border: solid 1px black !important;

  border-radius: $radius-md !important;

}



.eventPanelBar

  .k-panelbar-item

  .tabsGuestAdd

  .k-tabstrip-items-wrapper

  .k-active

  .k-link {

  @include typography-small;

  background-color: var(--vt-c-teal-dark) !important;

  color: white !important;

  border: solid 1px var(--vt-c-teal-dark) !important;

  border-radius: $radius-md !important;

}



.buttonAddGuest {

  text-align: right;

}



// Match the PhoneInputControl boxes to the sibling Kendo `rounded large` text

// boxes (Email / First Name / Last Name). The `.tabsGuestAdd` ancestor raises

// specificity above PhoneInputControl's scoped styles.

.tabsGuestAdd .guestPhoneField .phone-input-group {

  width: 100%;

}



.tabsGuestAdd .guestPhoneField .country-select,

.tabsGuestAdd .guestPhoneField .phone-input {

  @include typography-small;

  padding-top: $spacing-1;

  padding-bottom: $spacing-1;

}



.tabsGuestAdd .guestPhoneField .country-select {

  border-radius: $radius-md 0 0 $radius-md;

}



.tabsGuestAdd .guestPhoneField .phone-input {

  /* !important required: PhoneInputControl's scoped .phone-input declares border-radius with !important, so only !important can override it regardless of selector specificity */

  border-radius: 0 $radius-md $radius-md 0 !important;

}



.eventGuestPanel .k-animation-container {

  @include typography-small;

  width: 100% !important;

}



.btnAddGuest,

.btnSendEmail,

.btnDelete,

.btnTransfer,

.btnPlaceOrder {

  @include typography-small;

  @include font-weight-regular;

  display: inline-block;

  padding: $spacing-4 $spacing-8 !important;

  margin-bottom: 0;

  text-align: center;

  vertical-align: middle;

  cursor: pointer;

  border: none !important;

  white-space: nowrap;

  -webkit-user-select: none;

  -moz-user-select: none;

  -ms-user-select: none;

  -o-user-select: none;

  user-select: none;

  background: none repeat scroll 0 0 var(--vt-c-teal-dark);

  color: var(--vt-c-white);

  text-shadow: inherit !important;

  border-radius: $radius-md !important;

  @include font-weight-medium;

  font-style: normal;

  line-height: 20px;

  text-wrap: auto;

  text-align: center;

}



.btnDelete {

  background-color: #d4655d !important;

  margin-left: 10px;

}



.btnTransfer {

  background-color: #bdbdbd !important;

  margin-left: 10px;

}



.eventListLink {

  color: var(--vt-c-teal-dark) !important;

  text-decoration: underline !important;

}



.breadcrumb {

  @include typography-small;

}



.guest-order-grid-header .k-column-title,

.guest-grid-receipt-header .k-column-title,

.guest-grid-delete-header .k-column-title {

  display: none;

}



.guestGrid .k-column-title {

  white-space: normal;

}



/* Guest grid receipt icon - size to match delete icon (30px) */

.guest-grid-receipt .k-svg-icon,

.guest-grid-receipt .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,

.guest-grid-receipt .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,

.guest-grid-receipt-icon .k-svg-icon,

.guest-grid-receipt-icon.k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,

.guest-grid-receipt-icon.k-button-md.k-icon-button .k-button-icon.k-icon-md>svg

{

    width: 30px;

    height: 30px;

}



.guestGrid .guestSendEmailBtn .k-button-text {

  white-space: normal;

}



/* Grid buttons (E-Mail Senden, Bestellung Aufgeben) - consistent height for 2 rows */

.guestSendEmailBtn,

.btnPlaceOrder,

.hostPortalAccessBtn {

  text-transform: none !important;

  padding: 6px 12px !important;

  height: 48px !important;

  white-space: normal !important;

  line-height: 1.4 !important;

  display: inline-flex !important;

  align-items: center !important;

  justify-content: center !important;

}



/* Action buttons (Speichern, Löschen) - consistent height for 2 rows */

.btnSave,

.btnDelete {

  padding: $spacing-4 $spacing-8 !important;

  height: 48px !important;

  white-space: normal !important;

  line-height: 1.4 !important;

  display: inline-flex !important;

  align-items: center !important;

  justify-content: center !important;

}



.guestGrid .guest-role-cell span.host {

  padding-left: 10px !important;

}



.guestGrid .guest-role-cell .k-menu-link {

  padding-left: 0px !important;

}



.guestGrid .k-menu-expand-arrow {

  display: none !important;

}



/* Host Portal Styling */

.host-portal-header {

  @include typography-body;

  @include font-weight-bold;

  background: #fefefe;

  border: 1px solid #dee2e6;

  border-radius: $radius-md;

  padding: $spacing-2-5 $spacing-3;

}



.host-portal-step {

  @include typography-small;

  @include font-weight-bold;

  background: #fefefe;

  border: 1px solid #d9e3e5;

  border-radius: $radius-md;

  padding: $spacing-2 $spacing-2-5;

  margin-bottom: 10px;

}



.host-portal .form-control,

.host-portal .k-input-inner {

  @include typography-small;

}



.host-portal-preview img {

  max-width: 100%;

  height: auto;

}



/* Host Portal Preview Card */

.preview-card {

  background: var(--vt-c-white);

  overflow: hidden;

}

.preview-card-image {

  background: linear-gradient(135deg, #f0f4f7, #e9ecef);

  height: 120px;

}

.preview-card-title {

  @include typography-body;

  @include font-weight-semibold;

  padding: $spacing-2-5 $spacing-3 0 $spacing-3;

}

.preview-card-body {

  padding: $spacing-2 $spacing-3 $spacing-3 $spacing-3;

  overflow-x: hidden;

  word-wrap: break-word;

  @include typography-caption;

  word-break: break-word;

  white-space: pre-wrap;

  box-sizing: border-box;

}



.preview-card-body * {

  max-width: 100% !important;

  box-sizing: border-box;

}



/* Make Optional note textarea larger */

.host-portal textarea.form-control {

  min-height: 220px !important; /* override any height set by theme */

  height: 220px !important;     /* force initial height */

  resize: vertical;             /* allow user to grow/shrink */

  line-height: 1.4 !important; /* ensure proper line spacing */

}



/* Email Editor Form Styling */

.email-editor-form {

  background: var(--vt-c-white-soft);

  border: 1px solid #dee2e6;

  border-radius: $radius-md;

  padding: $card-padding;

}



.email-editor-form .row {

  align-items: center;

}



/* Email Preview Container */

.email-preview-container {

  background: var(--vt-c-white);

  max-height: 400px;

  overflow-y: auto;

  border: 1px solid #dee2e6 !important;

}



/* Ensure SweetAlert2 dialogs appear above Kendo UI dropdowns */

.swal2-container {

  z-index: $z-sweetalert !important;

}



.swal2-popup {

  z-index: $z-sweetalert-popup !important;

}





</style>

